// COSHH & Substances — per-template authored content. See ./types.ts for the content rule.
import type { TemplateSpecific } from "./types";

export const COSHH_SUBSTANCES: Record<string, TemplateSpecific> = {
  "coshh-general": {
    whoFor:
      "Use this RAMS when a principal contractor or client has asked you to show how hazardous substances are controlled within the method statement for a specific task, not in a separate folder. It suits trades who use a handful of products on a job — adhesives, sealants, primers, cleaning agents — and need the COSHH controls written into the same document that describes how the work is done. The angle here is integration: a reviewer should see the substance, its exposure route and the control sitting alongside the step that creates the risk.",
    scope:
      "This document covers the safe use, handling and storage of hazardous substances encountered during a defined construction task, with the COSHH assessment embedded in the task method statement rather than held separately. It applies where substances are used in small, controlled quantities by the work crew and where Safety Data Sheets are available for every product on site. It does not replace product-specific assessments for high-hazard substances such as silica, lead or isocyanates, which are covered by their own dedicated RAMS. The boundary is general substance control integrated into one task; it stops where a substance triggers its own statutory regime.",
    legislation: [
      "Control of Substances Hazardous to Health Regulations 2002 (as amended)",
      "Approved Code of Practice and guidance L5 (Sixth edition)",
      "EH40/2005 Workplace Exposure Limits (Fourth edition)",
      "Management of Health and Safety at Work Regulations 1999",
      "Chemicals (Hazard Information and Packaging for Supply) — CLP Regulation labelling",
    ],
    sequence: [
      "Collect the Safety Data Sheet for every product the task will use and list the hazardous substances by name and hazard statement.",
      "Map each substance to the step in the method statement where it is used, so the control sits next to the activity that creates exposure.",
      "Confirm the assigned controls for each step — ventilation, decanting method, quantity limits — before the substance is brought to the work area.",
      "Brief the crew at the task toolbox talk, walking through the embedded substance controls step by step.",
      "Carry out the work following the method, keeping containers closed when not in use and quantities to the day's requirement.",
      "Clean down and return surplus substances to controlled storage; segregate contaminated waste per the SDS disposal section.",
      "Record any deviation or new product introduced and update the embedded assessment before continuing.",
    ],
    hazards: [
      {
        hazard: "Substance exposure not linked to the task step that causes it",
        who: ["Operatives", "Adjacent trades"],
        initial: { likelihood: 4, severity: 3 },
        controls: [
          "Embed each substance control against the specific method step, not in a generic list",
          "Cross-reference every product to its SDS hazard statements before work starts",
          "Brief controls at the point of the step they protect during the toolbox talk",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
      {
        hazard: "Unassessed product introduced mid-task without an SDS",
        who: ["Operatives", "Supervisor"],
        initial: { likelihood: 3, severity: 3 },
        controls: [
          "Prohibit any product reaching site without a current Safety Data Sheet",
          "Hold a register of approved substances for the task at the work face",
          "Stop and re-assess before a substitute or new product is used",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
      {
        hazard: "Decanting and mixing creating uncontrolled vapour or splash",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 3 },
        controls: [
          "Decant in a ventilated area away from the general work crew",
          "Use the smallest practicable quantity for the day's work",
          "Keep lids on between uses and use a funnel or pump to limit splash",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
      {
        hazard: "Contaminated waste and empty containers mismanaged",
        who: ["Operatives", "Site cleaners"],
        initial: { likelihood: 3, severity: 2 },
        controls: [
          "Segregate substance-contaminated waste per the SDS disposal section",
          "Label and seal waste containers and keep them away from welfare areas",
          "Remove waste via a licensed carrier rather than general skips",
        ],
        residual: { likelihood: 1, severity: 2 },
      },
    ],
    ppe: [
      "Chemical-resistant gloves matched to the SDS",
      "Eye protection (goggles where splash is foreseeable)",
      "Overalls or aprons appropriate to the substance",
      "Footwear suitable for the work area",
    ],
    competence: [
      "Operatives briefed on the embedded COSHH controls for this task",
      "Supervisor able to read and apply a Safety Data Sheet",
      "Awareness of substance-specific first aid from the SDS",
    ],
    permits: ["No permit required unless the task area or client procedure imposes one"],
    equipment: [
      "Product Safety Data Sheets for every substance",
      "Approved-substance register for the task",
      "Decanting funnels and pumps",
      "Sealable labelled waste containers",
      "Spill kit sized to the quantities in use",
    ],
    pcChecks: [
      "That each substance is tied to the method step that uses it, not collected in a generic list at the back",
      "That a current SDS exists for every product named and the controls match the SDS advice",
      "That the assessment names a route to re-assess if a new product is introduced mid-task",
    ],
    faqs: [
      {
        q: "Can a COSHH assessment be part of the method statement instead of a separate document?",
        a: "Yes, and for many tasks it is clearer that way. COSHH 2002 requires a suitable and sufficient assessment, but it does not dictate the format. Embedding the substance controls against the relevant method step often makes the assessment more usable on site, because the operative reads the control at the moment they create the risk. Principal contractors generally accept this provided every substance is still individually assessed and the Safety Data Sheets are referenced.",
      },
      {
        q: "Do I need a COSHH assessment for products in small quantities?",
        a: "Quantity affects the level of risk, not whether an assessment is required. If a product carries a hazard pictogram or hazard statement on its label, COSHH applies regardless of how little you use. A small-quantity task may end up with very simple controls — gloves, ventilation, lids on — but the assessment still has to be recorded so the reasoning is auditable.",
      },
    ],
  },

  "coshh-risk-assessment": {
    whoFor:
      "This is for the person tasked with producing the COSHH risk assessments themselves — the supervisor, SHEQ coordinator or competent person who has to work through every substance on a job and record a defensible assessment for each. It walks the standalone assessment workflow: pulling substances from Safety Data Sheets, identifying exposure routes, checking workplace exposure limits and selecting controls. Use it when the deliverable a client wants is the assessment process and its records, separate from any one task method statement.",
    scope:
      "This document sets out the workflow for carrying out and recording standalone COSHH risk assessments across the substances used on a project. It covers identifying hazardous substances from SDSs, determining who is exposed and by what route, comparing exposures against EH40 workplace exposure limits, applying the hierarchy of control, and producing a per-substance assessment record. It does not itself authorise any physical work — it produces the assessments that task RAMS then reference. The boundary is the assessment activity and its documentation; site execution is governed by the relevant task method statement.",
    legislation: [
      "Control of Substances Hazardous to Health Regulations 2002 (as amended)",
      "Approved Code of Practice and guidance L5 (Sixth edition)",
      "EH40/2005 Workplace Exposure Limits (Fourth edition)",
      "REACH and CLP Regulations — Safety Data Sheet content and labelling",
      "COSHH Regulation 6 — assessment of health risks",
    ],
    sequence: [
      "Compile an inventory of every substance and process by-product on the project from delivery records and SDSs.",
      "For each substance, read the SDS sections on hazards, exposure controls and toxicology to identify the health effect and exposure route.",
      "Determine who could be exposed — operatives, adjacent trades, cleaners — and estimate frequency and duration of exposure.",
      "Compare the expected exposure against any EH40 workplace exposure limit and decide whether monitoring is needed.",
      "Select controls in hierarchy order, starting with elimination or substitution before engineering controls and RPE.",
      "Record the assessment per substance, with the conclusion, controls and any health surveillance trigger.",
      "Set a review date and re-assess when a substance, supplier or process changes.",
    ],
    hazards: [
      {
        hazard: "Substance missed from the inventory and never assessed",
        who: ["Assessor", "All exposed workers"],
        initial: { likelihood: 4, severity: 4 },
        controls: [
          "Build the inventory from delivery and procurement records, not memory",
          "Include process-generated substances such as dusts and fumes, not just supplied products",
          "Re-check the inventory at each project phase as new trades arrive",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard: "Exposure route misidentified leading to wrong controls",
        who: ["Assessor", "Operatives"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Read the SDS toxicology and exposure sections for every route — inhalation, skin, ingestion",
          "Assess each route separately rather than assuming inhalation dominates",
          "Match the control to the route, e.g. gloves for dermal, LEV for inhalation",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
      {
        hazard: "Exposure not checked against the EH40 workplace exposure limit",
        who: ["Assessor", "Operatives"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Look up each substance with a WEL in EH40/2005 and record the limit in the assessment",
          "Decide whether air monitoring is needed to demonstrate compliance",
          "Where no WEL exists, apply good control practice and the principles of good control",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard: "Assessment not reviewed when substances or processes change",
        who: ["Assessor", "Supervisor"],
        initial: { likelihood: 3, severity: 3 },
        controls: [
          "Set an explicit review date on every per-substance record",
          "Trigger a re-assessment on any product, supplier or method change",
          "Keep superseded assessments to show the change history",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
    ],
    ppe: [
      "PPE specified per substance by the completed assessments",
      "Reference PPE for handling SDSs and samples (gloves, eye protection)",
      "RPE only where the assessment concludes it is required",
    ],
    competence: [
      "Assessor competent in COSHH assessment under Regulation 6",
      "Ability to interpret a Safety Data Sheet and EH40 entries",
      "Understanding of the hierarchy of control and when monitoring is needed",
    ],
    permits: ["No permit required for the assessment activity itself"],
    equipment: [
      "Substance inventory drawn from procurement records",
      "Safety Data Sheet library for all products",
      "EH40/2005 workplace exposure limit tables",
      "Per-substance assessment record template",
      "Exposure monitoring equipment where the assessment calls for it",
    ],
    pcChecks: [
      "That there is a separate recorded assessment for each substance, not one blanket COSHH statement covering everything",
      "That WELs from EH40 are quoted for substances that have them, with a stated monitoring decision",
      "That process-generated substances such as dusts and fumes appear in the inventory, not only supplied products",
    ],
    faqs: [
      {
        q: "What is a workplace exposure limit and where do I find it?",
        a: "A workplace exposure limit (WEL) is the maximum concentration of an airborne substance, averaged over a reference period, to which workers may be exposed. WELs are published in HSE document EH40/2005 and are usually given as an 8-hour time-weighted average and, for some substances, a 15-minute short-term limit. Your COSHH assessment should record the relevant WEL for each substance that has one and explain how you keep exposure below it. Where a substance has no WEL, you still have to apply the principles of good control practice.",
      },
      {
        q: "How often should a COSHH assessment be reviewed?",
        a: "COSHH requires the assessment to be reviewed regularly and whenever there is reason to suspect it is no longer valid. In practice that means re-assessing if you change a product, supplier, quantity or work method, if monitoring shows controls are failing, or if there is a near miss or ill-health report. Many assessors also set a periodic review date, commonly annually, as a backstop. The trigger that matters most is change — a static assessment for a changed process is no longer suitable and sufficient.",
      },
    ],
  },

  "coshh-silica": {
    whoFor:
      "Cutting, grinding, drilling or chasing concrete, stone, brick or screed releases respirable crystalline silica, and principal contractors now expect a dedicated silica RAMS rather than a generic dust line. This document is for any trade generating dust from silica-containing materials who needs to show on-tool extraction, water suppression and the right RPE protection factor. It assumes regular or foreseeable exposure, which brings health surveillance into scope.",
    scope:
      "This RAMS covers the control of respirable crystalline silica (RCS) generated when cutting, grinding, drilling or abrading silica-bearing materials on a construction site. It addresses dust suppression at source, on-tool extraction, respiratory protection and health surveillance for workers with regular exposure. It does not cover abrasive blasting or tunnelling, where exposures and controls differ and additional assessment is required. The boundary is power-tool work on silica materials with engineering controls applied at the tool.",
    legislation: [
      "Control of Substances Hazardous to Health Regulations 2002 (as amended)",
      "EH40/2005 — RCS workplace exposure limit 0.1 mg/m³ (8-hr TWA, respirable)",
      "COSHH Regulation 11 — health surveillance",
      "HSE guidance on construction dust (CIS36) and silica",
      "INDG479 — guidance on respirator face-fit testing",
    ],
    sequence: [
      "Select the lowest-dust method first — order cut-to-size materials or use a non-powered alternative where the design allows.",
      "Fit water suppression or an M- or H-class on-tool extraction unit matched to the tool before any cutting begins.",
      "Confirm operatives wear RPE with an adequate assigned protection factor and have a current face-fit certificate.",
      "Isolate or screen the work area so RCS does not migrate to other trades, and restrict access.",
      "Carry out the cutting or grinding with suppression or extraction running continuously, never dry without controls.",
      "Clean down using H-class vacuum or wet methods — never dry sweeping or compressed air.",
      "Record exposure-relevant work for the health surveillance programme and review controls against the 0.1 mg/m³ limit.",
    ],
    hazards: [
      {
        hazard: "Inhalation of respirable crystalline silica causing silicosis or lung cancer",
        who: ["Operatives", "Adjacent trades"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Eliminate dry cutting — use water suppression or M/H-class on-tool extraction at source",
          "Provide RPE with an assigned protection factor of at least 20 and current face-fit",
          "Screen and restrict the area so RCS does not reach other workers",
          "Keep exposure below the 0.1 mg/m³ respirable WEL and verify by monitoring",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard: "On-tool extraction unit of the wrong class or poorly maintained",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Specify M-class as a minimum, H-class for the finest dusts",
          "Check filter condition and airflow before each shift",
          "Replace filters and dispose of collected dust as hazardous waste",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard: "RPE failing to protect because of poor fit or wrong type",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Face-fit test each wearer to the specific mask per INDG479",
          "Prohibit beards or stubble under tight-fitting masks",
          "Inspect and store RPE correctly between uses",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard: "Settled silica dust re-entrained during clean-down",
        who: ["Operatives", "Site cleaners"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Ban dry sweeping and compressed air for clearing dust",
          "Use H-class vacuum or wet cleaning methods only",
          "Damp down and bag debris before removal",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
    ],
    ppe: [
      "Tight-fitting RPE with assigned protection factor of at least 20 (face-fit tested)",
      "Disposable coveralls to prevent dust carry-over",
      "Eye protection against fragments and dust",
      "Gloves and cut-resistant gloves for handling cut materials",
    ],
    competence: [
      "Operatives trained in silica dust control and tool-mounted extraction",
      "Current face-fit certificate for the RPE in use",
      "Supervisor able to verify extraction class and airflow",
    ],
    permits: ["Hot works permit where cutting generates sparks near combustibles"],
    equipment: [
      "Water-suppression cutting equipment or M/H-class on-tool extraction",
      "H-class industrial vacuum for clean-down",
      "Face-fit tested powered or tight-fitting respirators",
      "Dust screens and area barriers",
      "Hazardous-waste bags for collected dust",
    ],
    pcChecks: [
      "A per-task dust assessment, not one generic 'dust' line — reviewers look for the on-tool extraction class (M or H) and the RPE assigned protection factor",
      "That face-fit certificates exist for the named operatives and the work is treated as no-beard",
      "That regular or foreseeable exposure has triggered enrolment in health surveillance under COSHH Regulation 11",
    ],
    faqs: [
      {
        q: "What is the workplace exposure limit for silica dust?",
        a: "The workplace exposure limit for respirable crystalline silica is 0.1 mg/m³ as an 8-hour time-weighted average, published in EH40/2005. This is a low limit and it is easily exceeded by uncontrolled dry cutting, which is why on-tool extraction or water suppression is essential rather than optional. Where there is any doubt that controls keep exposure below the limit, air monitoring should be carried out to demonstrate compliance. Because silica causes irreversible disease, the assessment should aim to keep exposure as far below the limit as is reasonably practicable, not just at it.",
      },
      {
        q: "Do silica workers need health surveillance?",
        a: "Yes, where exposure is regular or otherwise foreseeable, COSHH Regulation 11 requires health surveillance. For silica this typically means a respiratory questionnaire and, depending on risk, periodic lung-function testing administered through an occupational health provider. The purpose is to catch early signs of harm and to confirm that the control measures in your RAMS are actually working. Records have to be kept for an extended period because silica-related disease can develop years after exposure.",
      },
    ],
  },

  "coshh-cement": {
    whoFor:
      "Groundworkers, concretors and bricklayers handling wet cement and concrete face two distinct hazards a generic dust assessment misses: alkaline burns from prolonged skin contact and chromium VI sensitisation. This RAMS is for crews pouring, screeding, laying or finishing concrete who kneel or stand in wet mix, and for anyone batching or mixing dry cement powder. Principal contractors increasingly ask for cement to be assessed in its own right after documented amputation-level burn injuries.",
    scope:
      "This document covers the handling, mixing, placing and finishing of cement and wet concrete on groundworks and structural tasks. It addresses skin contact with the wet alkaline mix, chromium VI sensitisation, and irritant dust from dry cement during batching. It does not cover the operation of concrete pumps or large-scale batching plant, which require their own plant-specific assessments. The boundary is hands-on placement and finishing of cementitious materials by the work crew.",
    legislation: [
      "Control of Substances Hazardous to Health Regulations 2002 (as amended)",
      "Approved Code of Practice and guidance L5 (Sixth edition)",
      "EH40/2005 — Portland cement workplace exposure limits (dust)",
      "REACH restriction on chromium VI content in cement (water-soluble Cr(VI) limited and reducing agent required)",
      "Management of Health and Safety at Work Regulations 1999",
    ],
    sequence: [
      "Plan the pour so the crew works from outside the mix where possible and minimise time kneeling or standing in wet concrete.",
      "Open and decant dry cement carefully to limit airborne dust during batching and mixing.",
      "Issue and fit waterproof gloves and waterproof trousers or kneeling protection before any contact with wet mix.",
      "Place and screed the concrete, keeping skin covered and avoiding wet mix entering boots or gloves.",
      "Stop immediately and wash off any skin contact with clean water; do not let wet concrete sit against skin.",
      "Finish, then clean tools and remove contaminated clothing before welfare breaks.",
      "Wash thoroughly at the end of the task and report any persistent redness, burning or rash.",
    ],
    hazards: [
      {
        hazard: "Alkaline burns from prolonged skin contact with wet concrete",
        who: ["Concretors", "Groundworkers"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Avoid kneeling or standing in wet concrete; work from outside the mix where the pour allows",
          "Wear waterproof gloves and waterproof trousers or impervious kneeling protection",
          "Wash any skin contact off immediately with clean water and never let mix sit against skin",
          "Provide clean water and washing facilities at the work face",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard: "Chromium VI sensitisation causing allergic contact dermatitis",
        who: ["Concretors", "Bricklayers"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Use cement within the supplier's shelf life so the chromate-reducing agent remains effective",
          "Prevent skin contact with waterproof gloves rather than relying on barrier creams",
          "Identify previously sensitised workers and keep them off cement contact tasks",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard: "Irritant cement dust from dry mixing and batching",
        who: ["Operatives mixing dry cement"],
        initial: { likelihood: 3, severity: 3 },
        controls: [
          "Decant and pour bags slowly to limit dust release",
          "Mix in a ventilated area away from the crew where practicable",
          "Wear FFP3 RPE and eye protection when handling dry cement powder",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
      {
        hazard: "Wet concrete entering boots or gloves and held against skin unnoticed",
        who: ["Concretors", "Groundworkers"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Tape or seal glove and boot openings against ingress",
          "Check footwear and gloves through the pour and change if mix gets inside",
          "Brief the crew that a burn can develop without immediate pain",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
    ],
    ppe: [
      "Waterproof gloves resistant to alkaline mix",
      "Waterproof trousers and impervious kneeling pads or boards",
      "Wellington boots tall enough to keep mix out",
      "Eye protection and FFP3 RPE when handling dry cement",
    ],
    competence: [
      "Operatives briefed on cement burn risk and that pain may be delayed",
      "Supervisor aware of chromium VI sensitisation and shelf-life requirement",
      "Trained in correct use of waterproof PPE for concrete work",
    ],
    permits: ["No permit required unless the pour location or client procedure imposes one"],
    equipment: [
      "Waterproof concreting gloves and trousers",
      "Kneeling boards or impervious knee protection",
      "Clean-water supply and eyewash at the work face",
      "Screeds, floats and trowels for placement and finishing",
      "FFP3 masks for dry-cement handling",
    ],
    pcChecks: [
      "That wet concrete is assessed as a corrosive alkaline skin hazard, not just as nuisance dust — waterproof gloves and trousers must be specified",
      "That clean water and washing facilities are provided at the work face for immediate decontamination of skin contact",
      "That chromium VI sensitisation and cement shelf life are addressed for repeat-exposure crews",
    ],
    faqs: [
      {
        q: "Why are barrier creams not enough for working with wet concrete?",
        a: "Wet concrete is strongly alkaline and can cause serious chemical burns after prolonged contact, and there are documented cases severe enough to require skin grafts or amputation. Barrier creams give a false sense of security because they rub off, are applied unevenly and do not stop the high-pH mix reaching skin. The reliable control is a physical impervious barrier — waterproof gloves and waterproof trousers — backed by immediate washing if mix does contact skin. Barrier cream may have a minor supporting role but it should never be the primary control in a RAMS.",
      },
      {
        q: "Can wet concrete cause burns even if it does not hurt at the time?",
        a: "Yes, and that is exactly why cement burns are so dangerous. The alkaline mix attacks skin slowly and the worker often feels little or nothing until significant damage has already occurred, sometimes hours after the contact. Kneeling or standing in wet concrete with mix inside a boot or glove is a common cause of severe burns precisely because it goes unnoticed. The RAMS should make clear that any skin contact must be washed off immediately regardless of whether it hurts.",
      },
    ],
  },

  "coshh-solvents": {
    whoFor:
      "Painters, decorators and finishing trades using solvent-based paints, thinners, primers and two-pack systems need a RAMS that handles both the flammable and the health risks together. This document is for crews spraying or brushing solvent products in occupied or poorly ventilated interiors, and for anyone using 2-pack isocyanate-containing paints. A principal contractor will look for DSEAR storage controls alongside the vapour and asthma controls.",
    scope:
      "This RAMS covers the use of solvent-based paints, thinners and two-pack coatings during interior and exterior finishing works. It addresses flammable storage and ignition control under DSEAR, vapour inhalation in enclosed spaces, isocyanate exposure from 2-pack products, and disposal of solvent waste. It does not cover large-scale industrial spray-booth operations, which need dedicated ventilation and assessment. The boundary is brush, roller and small-scale spray application by the finishing crew.",
    legislation: [
      "Control of Substances Hazardous to Health Regulations 2002 (as amended)",
      "Dangerous Substances and Explosive Atmospheres Regulations 2002 (DSEAR)",
      "EH40/2005 — workplace exposure limits for solvents and isocyanates",
      "COSHH Regulation 11 — health surveillance for isocyanate exposure",
      "REACH and CLP — Safety Data Sheets and flammable labelling",
    ],
    sequence: [
      "Read the SDS for each product and identify flammability, solvent vapour hazards and any isocyanate content in 2-pack systems.",
      "Store only the day's quantity at the work area, keeping the bulk in a ventilated, fire-rated store away from ignition sources.",
      "Provide and confirm ventilation in the room before opening containers — mechanical extraction where natural ventilation is poor.",
      "Mix two-pack products strictly to the SDS ratio in a ventilated area, wearing the specified RPE for isocyanates.",
      "Apply the coating, keeping ignition sources out and lids on containers between uses.",
      "Ventilate during drying and curing, restricting access while solvent vapour persists.",
      "Collect solvent waste and contaminated rags in sealed metal containers and remove via a licensed waste carrier.",
    ],
    hazards: [
      {
        hazard: "Flammable solvent vapour igniting (fire or explosive atmosphere)",
        who: ["Operatives", "Other site occupants"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Store bulk flammables in a ventilated fire-rated store, day quantities only at the work face",
          "Eliminate ignition sources — no hot works, smoking or sparking tools in the area",
          "Ventilate to keep vapour below the lower explosive limit per DSEAR",
          "Keep containers closed and bond/earth where decanting larger volumes",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard: "Isocyanate exposure from two-pack products causing occupational asthma",
        who: ["Painters/sprayers"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Substitute a non-isocyanate or water-based system where the specification allows",
          "Mix and apply with mechanical extraction and air-fed or appropriate RPE",
          "Enrol exposed workers in health surveillance under COSHH Regulation 11",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard: "Solvent vapour build-up in poorly ventilated rooms",
        who: ["Operatives", "Building occupants"],
        initial: { likelihood: 4, severity: 4 },
        controls: [
          "Assess ventilation before work and add mechanical extraction where natural airflow is poor",
          "Limit the area worked at once and rotate operatives to reduce exposure time",
          "Wear organic-vapour RPE where ventilation cannot keep exposure below the WEL",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard: "Solvent-soaked rags self-heating or igniting in waste",
        who: ["Operatives", "Site cleaners"],
        initial: { likelihood: 2, severity: 4 },
        controls: [
          "Place used rags in a sealed metal waste container, not open bags",
          "Remove solvent waste daily via a licensed carrier",
          "Keep waste containers away from heat and welfare areas",
        ],
        residual: { likelihood: 1, severity: 3 },
      },
    ],
    ppe: [
      "Organic-vapour or air-fed RPE matched to the solvent and isocyanate hazard",
      "Solvent-resistant nitrile gloves",
      "Eye protection against splash and spray",
      "Anti-static or non-sparking footwear where flammable atmospheres are possible",
    ],
    competence: [
      "Operatives trained in solvent and two-pack product use",
      "Face-fit certificate where tight-fitting RPE is used",
      "Supervisor competent in DSEAR storage and ignition control",
    ],
    permits: ["Hot works permit and exclusion of ignition sources where flammable atmospheres may form"],
    equipment: [
      "Fire-rated flammable storage cabinet",
      "Mechanical ventilation or local extraction units",
      "Sealed metal waste containers for solvent rags",
      "Organic-vapour or air-fed respirators",
      "Brushes, rollers and small-scale spray equipment",
    ],
    pcChecks: [
      "That flammable storage and ignition control under DSEAR are addressed, not just the health hazard",
      "That two-pack products are flagged for isocyanate content with extraction, RPE and health surveillance specified",
      "That solvent waste and contaminated rags have a defined sealed-container and licensed-disposal route",
    ],
    faqs: [
      {
        q: "Do water-based paints need a COSHH assessment?",
        a: "Often yes, though usually a simpler one. Water-based does not automatically mean hazard-free — many emulsions contain biocides, coalescing agents or trace solvents that carry hazard statements on the label. The deciding factor is the Safety Data Sheet: if the product carries a hazard pictogram or statement, COSHH applies. Where a water-based product genuinely has no hazardous classification the assessment can be brief, but you should still record that you checked the SDS and reached that conclusion.",
      },
      {
        q: "What extra controls do two-pack (2K) paints need?",
        a: "Two-pack systems frequently contain isocyanates, which are a leading cause of occupational asthma in the trade, so they need controls well beyond ordinary solvent paints. Spraying isocyanate products usually requires mechanical extraction and air-fed respiratory protection, and exposed workers should be under health surveillance through COSHH Regulation 11. Mixing must follow the SDS ratio in a ventilated area, and substituting a non-isocyanate or water-based system should be considered first where the specification allows. Brush and roller application reduces airborne exposure compared with spraying but does not remove the need for RPE and ventilation.",
      },
    ],
  },

  "coshh-lead": {
    whoFor:
      "Refurbishment and finishing trades disturbing old paint on pre-1970s buildings can release lead, which is governed by its own statutory regime — the Control of Lead at Work Regulations 2002, not just COSHH. This RAMS is for painters, decorators and maintenance crews preparing or removing lead-based paint by methods that generate dust or fume. It builds in work-category determination, blood-lead surveillance and decontamination to stop take-home exposure to workers' families.",
    scope:
      "This document covers non-licensed work that disturbs lead-based paint during refurbishment and redecoration, including dry abrasion, scraping and heat-based removal where these are permitted. It addresses airborne lead control, the CLAW work-category decision, blood-lead medical surveillance, and hygiene measures to prevent take-home contamination. It does not cover lead smelting, burning or hot cutting of lead sheet, which trigger different and higher CLAW duties and are excluded. The boundary is paint disturbance assessed under the non-licensed CLAW regime.",
    legislation: [
      "Control of Lead at Work Regulations 2002 (as amended)",
      "Approved Code of Practice and guidance L132 — Control of lead at work",
      "Control of Substances Hazardous to Health Regulations 2002 (general principles)",
      "EH40/2005 — lead occupational exposure limit (airborne)",
      "CLAW blood-lead action and suspension levels for medical surveillance",
    ],
    sequence: [
      "Confirm the paint contains lead by testing or by age of the building, and determine the CLAW work category for the chosen method.",
      "Select the lowest-exposure removal method — wet stripping or chemical removal in preference to dry sanding or burning.",
      "Set up the work area with sheeting and controlled access, segregated from welfare and from other trades.",
      "Apply controls during removal — water suppression or H-class extraction — and wear the specified RPE and disposable coveralls.",
      "Prohibit eating, drinking and smoking in the work area to cut ingestion of lead dust.",
      "Decontaminate before breaks and at the end of the task — remove coveralls, wash hands and face, and prevent contaminated clothing leaving site.",
      "Bag lead waste as hazardous, clean the area by H-class vacuum or wet methods, and arrange blood-lead surveillance for those significantly exposed.",
    ],
    hazards: [
      {
        hazard: "Inhalation or ingestion of lead dust and fume causing systemic lead poisoning",
        who: ["Operatives"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Prefer wet or chemical stripping over dry sanding or heat that creates dust and fume",
          "Use H-class extraction or water suppression and provide adequate RPE",
          "Ban eating, drinking and smoking in the work area to prevent ingestion",
          "Determine the CLAW work category and apply controls to keep airborne lead below the OEL",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard: "Take-home exposure contaminating workers' families",
        who: ["Operatives", "Workers' families"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Use disposable coveralls and remove them before leaving the work area",
          "Provide wash and changing facilities; prohibit work clothing being worn or laundered at home",
          "Store contaminated PPE separately and dispose of it as hazardous waste",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
      {
        hazard: "Failure to identify significant exposure and arrange blood-lead surveillance",
        who: ["Operatives", "Supervisor"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Assess whether exposure is significant and enrol workers in medical surveillance under CLAW",
          "Act on blood-lead action and suspension levels — suspend workers who reach the suspension level",
          "Keep medical and air-monitoring records as required by CLAW",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard: "High-exposure method used without the correct work-category controls",
        who: ["Operatives"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Avoid dry sanding and hot-air burning of lead paint where lower-dust methods exist",
          "Match controls to the determined CLAW work category before starting",
          "Stop and re-assess if the method changes to a more dust-generating one",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
    ],
    ppe: [
      "RPE with adequate protection factor for lead dust/fume (face-fit tested)",
      "Disposable coveralls removed before leaving the work area",
      "Lead-resistant gloves",
      "Eye protection during dry removal methods",
    ],
    competence: [
      "Operatives trained in lead paint work and CLAW requirements",
      "Supervisor able to determine the CLAW work category and exposure significance",
      "Current face-fit certificate for the RPE in use",
    ],
    permits: ["Hot works permit where heat-based paint removal is used near combustibles"],
    equipment: [
      "Wet or chemical paint-stripping equipment",
      "H-class vacuum for clean-down",
      "Disposable coveralls and decontamination/washing facilities",
      "Hazardous-waste sacks for lead debris",
      "Air-monitoring and blood-lead surveillance arrangements",
    ],
    pcChecks: [
      "That the work is assessed under CLAW 2002, not generic COSHH, with the work category determined for the removal method",
      "That medical (blood-lead) surveillance and the action/suspension levels are addressed where exposure is significant",
      "That decontamination and clothing controls are in place to prevent take-home exposure to families",
    ],
    faqs: [
      {
        q: "Can I remove lead paint by sanding?",
        a: "Dry sanding is one of the worst methods because it generates large amounts of fine, easily inhaled lead dust, and it should be avoided wherever a lower-exposure method exists. Wet stripping or chemical removal is generally preferred because it keeps the lead bound in liquid or paste rather than airborne. If an abrasive method is unavoidable it must use H-class on-tool extraction and a determined CLAW work category, with appropriate RPE and decontamination. Power sanding of lead paint without these controls is not acceptable.",
      },
      {
        q: "When does lead paint work need blood-lead medical surveillance?",
        a: "Under the Control of Lead at Work Regulations 2002, medical surveillance including blood-lead monitoring is required when a worker's exposure is significant — broadly when airborne lead could exceed the action level or there is a real risk of ingestion. The regulations set action levels and suspension levels for blood lead, and a worker who reaches the suspension level must be taken off lead work until their levels fall. Surveillance is arranged through an appointed doctor or HSE-approved scheme, and records must be kept. Even where surveillance is not strictly triggered, hygiene and decontamination controls still apply to keep exposure low.",
      },
    ],
  },
};
